"use client";

import { create } from "zustand";
import { createJSONStorage, persist } from "zustand/middleware";
import type { StateStorage } from "zustand/middleware";
import { getActiveStoreId } from "@/modules/store/stores/useActiveStore";
// Import direto do contrato, não do barrel: o `index` do módulo reexporta componentes
// (ShippingQuoteBox → big2be-ui → next/image), e o store é consumido em teste de nó puro.
import { optionKey } from "@/modules/shipping/types/shipping.dto";
import type { AvailableSlot, ShippingOption } from "@/modules/shipping";
import type { CreditCardInput, OrderConfirmation } from "../types/checkout.dto";
import type { CardValidationErrors } from "../lib/card-validation";
import type {
    CartLineItem,
    CheckoutAddress,
    PaymentDraft,
    ReceiverData,
    ReceiverDraft,
    ReceiverErrors,
    ReceiverMode,
} from "../types/checkout";

/** Recebedor em branco. */
const EMPTY_RECEIVER: ReceiverData = { name: "", phone: "" };

/** Cartão em branco — valor inicial do formulário e limpeza pós-pedido. */
export const EMPTY_CARD: CreditCardInput = {
    cardNumber: "",
    cardholderName: "",
    expirationMonth: "",
    expirationYear: "",
    cvv: "",
    cpf: "",
};

/**
 * O que o rascunho GRAVA (sessionStorage) — tudo que a pessoa preencheu e não
 * pode perder ao navegar entre as etapas nem ao recarregar a página.
 */
interface CheckoutDraft {
    /** Endereço de entrega escolhido. */
    address: CheckoutAddress | null;
    /** Opção de frete escolhida (para o endereço acima). */
    shipping: ShippingOption | null;
    /** Faixa de data/horário — só quando o frete acima é agendado. */
    slot: AvailableSlot | null;
    /** Quem recebe — um par de valores por modo (ver `ReceiverDraft`). */
    receiver: ReceiverDraft;
    /** Escolhas não-sensíveis do pagamento (método + parcelas). */
    payment: PaymentDraft;
    /** Observação geral do pedido (vai para `ecm__order.comment`). */
    observations: string;
    /** Chave de idempotência da tentativa de compra em curso. */
    orderAttemptKey: string | null;
    /** Confirmação do pedido fechado (preenchida na etapa de pagamento). */
    confirmation: OrderConfirmation | null;
    /** Snapshot dos itens do pedido para exibir na conclusão. */
    orderItems: CartLineItem[];
    /** Rótulo da forma de pagamento escolhida (para a conclusão). */
    paymentLabel: string;
}

/**
 * O que de fato vai para o sessionStorage: o rascunho + a loja em que ele foi
 * feito. O `storeId` não é estado de aplicação (ninguém lê durante o uso) —
 * existe só para o `merge` reconhecer e descartar rascunho de outra loja.
 */
type PersistedDraft = CheckoutDraft & { storeId: number };

/**
 * Estado que vive SÓ em memória — nunca chega ao sessionStorage.
 *
 * Os dados do cartão estão aqui de propósito: número, CVV e CPF do titular não
 * podem ser gravados em storage do browser (PCI-DSS proíbe persistir PAN/CVV, e
 * storage é legível por qualquer script da origem). Ficar no store em memória
 * ainda resolve o problema real de UX — ir ao endereço e voltar não perde o
 * cartão digitado —, e um reload limpa tudo.
 */
interface CheckoutVolatile {
    card: CreditCardInput;
    cardErrors: CardValidationErrors | null;
    receiverErrors: ReceiverErrors | null;
}

interface CheckoutActions {
    setAddress: (address: CheckoutAddress | null) => void;
    setShipping: (shipping: ShippingOption) => void;
    /** Reconcilia a opção escolhida com uma cotação nova (ver implementação). */
    syncShippingOptions: (options: ShippingOption[]) => void;
    /** Faixa agendada do frete escolhido (`null` = ainda não escolheu / limpou). */
    setSlot: (slot: AvailableSlot | null) => void;

    setReceiverMode: (mode: ReceiverMode) => void;
    /** Edita o recebedor do modo ATIVO (e limpa o erro dos campos tocados). */
    patchReceiver: (patch: Partial<ReceiverData>) => void;
    /** Semeia o modo "eu recebo" com o perfil, sem sobrescrever o que já tem. */
    prefillSelf: (values: Partial<ReceiverData>) => void;
    setReceiverErrors: (errors: ReceiverErrors | null) => void;

    setPaymentMethodId: (methodId: number | null) => void;
    setInstallments: (installments: number) => void;
    setObservations: (observations: string) => void;

    patchCard: (patch: Partial<CreditCardInput>) => void;
    setCardErrors: (errors: CardValidationErrors | null) => void;

    /** Chave da tentativa em curso (cria na primeira chamada, reusa depois). */
    beginOrderAttempt: () => string;
    /** Encerra a tentativa: o próximo envio é um pedido NOVO. */
    endOrderAttempt: () => void;

    setConfirmation: (
        confirmation: OrderConfirmation,
        items: CartLineItem[],
        paymentLabel: string
    ) => void;
    reset: () => void;
}

export type CheckoutState = CheckoutDraft & CheckoutVolatile & CheckoutActions;

const INITIAL_DRAFT: CheckoutDraft = {
    address: null,
    shipping: null,
    slot: null,
    receiver: { mode: "self", self: EMPTY_RECEIVER, other: EMPTY_RECEIVER },
    payment: { methodId: null, installments: 1 },
    observations: "",
    orderAttemptKey: null,
    confirmation: null,
    orderItems: [],
    paymentLabel: "",
};

const INITIAL_VOLATILE: CheckoutVolatile = {
    card: EMPTY_CARD,
    cardErrors: null,
    receiverErrors: null,
};

/** UUID v4 quando disponível; fallback simples para contextos sem crypto. */
function newAttemptKey(): string {
    return (
        globalThis.crypto?.randomUUID?.() ??
        `${Date.now().toString(36)}-${Math.random().toString(36).slice(2)}`
    );
}

/** Storage inerte para o SSR — não existe checkout no servidor. */
const SSR_STORAGE: StateStorage = {
    getItem: () => null,
    setItem: () => undefined,
    removeItem: () => undefined,
};

/**
 * Estado do checkout que atravessa os passos (carrinho → entrega → pagamento →
 * conclusão), persistido no **sessionStorage**.
 *
 * Por que sessionStorage e não localStorage: o rascunho carrega PII (nome e
 * telefone do recebedor, endereço completo). sessionStorage é por ABA e morre
 * quando ela fecha; localStorage deixaria o endereço de alguém em disco por
 * tempo indeterminado, num navegador que pode ser compartilhado.
 *
 * Por que persistir: antes o estado vivia só em memória e QUALQUER reload (ou
 * um F5 na etapa de pagamento) devolvia a pessoa para a entrega com os campos
 * em branco. Agora só o dado sensível se perde no reload — por decisão.
 *
 * `skipHydration`: a reidratação acontece num effect (ver `useCheckoutHydrated`)
 * para o primeiro render do cliente ser igual ao do servidor — ler
 * sessionStorage durante o render causaria erro de hidratação do React.
 *
 * Trocar o endereço zera o frete (a cotação é por endereço).
 */
export const useCheckoutStore = create<CheckoutState>()(
    persist(
        (set, get) => ({
            ...INITIAL_DRAFT,
            ...INITIAL_VOLATILE,

            // Trocar o endereço zera frete e faixa (a cotação é por endereço).
            setAddress: (address) => set({ address, shipping: null, slot: null }),
            // A faixa é DO método: trocar de método a descarta; reescolher o
            // mesmo (ou recotar com preço novo) mantém.
            setShipping: (shipping) =>
                set((state) => ({
                    shipping,
                    slot:
                        state.shipping && optionKey(state.shipping) === optionKey(shipping)
                            ? state.slot
                            : null,
                })),
            setSlot: (slot) => set({ slot }),

            syncShippingOptions: (options) => {
                const current = get().shipping;

                // Cotação vazia: não há frete válido para este endereço.
                if (options.length === 0) {
                    if (current) set({ shipping: null, slot: null });
                    return;
                }

                if (current) {
                    // Mesma opção: re-grava a versão NOVA — o preço pode ter
                    // mudado desde a cotação anterior, e fechar o pedido com o
                    // preço velho mostraria um total que o back não cobra.
                    // Sumiu da lista: limpa (e a faixa junto), e a pessoa escolhe de novo.
                    // Transportadora: um método rende várias opções, então reencontrar pelo
                    // methodId traria o serviço errado (SEDEX no lugar do PAC).
                    const chave = optionKey(current);
                    const next = options.find((o) => optionKey(o) === chave) ?? null;
                    set({ shipping: next, slot: next ? get().slot : null });
                    return;
                }

                // Nada escolhido ainda: pré-seleciona a mais barata.
                set({
                    shipping: options.reduce((min, o) => (o.price < min.price ? o : min)),
                    slot: null,
                });
            },

            setReceiverMode: (mode) =>
                set((state) => ({
                    receiver: { ...state.receiver, mode },
                    // Os erros eram do outro modo — não valem para este.
                    receiverErrors: null,
                })),

            patchReceiver: (patch) =>
                set((state) => {
                    const { mode } = state.receiver;
                    const errors = state.receiverErrors ? { ...state.receiverErrors } : null;
                    // Editar um campo limpa o erro DAQUELE campo (revalida ao avançar).
                    if (errors) {
                        for (const key of Object.keys(patch)) {
                            delete errors[key as keyof ReceiverErrors];
                        }
                    }

                    return {
                        receiver: {
                            ...state.receiver,
                            [mode]: { ...state.receiver[mode], ...patch },
                        },
                        receiverErrors: errors && Object.keys(errors).length > 0 ? errors : null,
                    };
                }),

            prefillSelf: (values) => {
                const { receiver } = get();
                const { self } = receiver;
                // Campo a campo, e só onde está vazio: se o fetch do perfil
                // chega depois de a pessoa ter digitado, o que ela escreveu vence.
                const next: ReceiverData = {
                    name: self.name.trim() ? self.name : (values.name?.trim() ?? ""),
                    phone: self.phone.trim() ? self.phone : (values.phone?.trim() ?? ""),
                };
                if (next.name === self.name && next.phone === self.phone) return;
                set({ receiver: { ...receiver, self: next } });
            },

            setReceiverErrors: (receiverErrors) => set({ receiverErrors }),

            setPaymentMethodId: (methodId) => {
                const { payment } = get();
                if (methodId === payment.methodId) return;
                // Trocar de método zera parcelamento e erros: a escolha "6x" e
                // o "CVV inválido" eram do método anterior.
                set({ payment: { methodId, installments: 1 }, cardErrors: null });
            },

            setInstallments: (installments) =>
                set((state) => ({ payment: { ...state.payment, installments } })),

            setObservations: (observations) => set({ observations }),

            patchCard: (patch) =>
                set((state) => {
                    const errors = state.cardErrors ? { ...state.cardErrors } : null;
                    // Mesma regra do recebedor: editar limpa o erro do campo.
                    if (errors) {
                        for (const key of Object.keys(patch)) {
                            delete errors[key as keyof CardValidationErrors];
                        }
                    }

                    return {
                        card: { ...state.card, ...patch },
                        cardErrors: errors && Object.keys(errors).length > 0 ? errors : null,
                    };
                }),

            setCardErrors: (cardErrors) => set({ cardErrors }),

            beginOrderAttempt: () => {
                const existing = get().orderAttemptKey;
                if (existing) return existing;
                const key = newAttemptKey();
                set({ orderAttemptKey: key });
                return key;
            },

            endOrderAttempt: () => set({ orderAttemptKey: null }),

            setConfirmation: (confirmation, orderItems, paymentLabel) =>
                set({
                    confirmation,
                    orderItems,
                    paymentLabel,
                    // Pedido fechado: os dados do cartão não têm mais por que
                    // continuar na memória do browser, e a tentativa terminou.
                    card: EMPTY_CARD,
                    cardErrors: null,
                    orderAttemptKey: null,
                }),

            reset: () => set({ ...INITIAL_DRAFT, ...INITIAL_VOLATILE }),
        }),
        {
            name: "checkout-draft",
            version: 1,
            storage: createJSONStorage(() =>
                typeof window === "undefined" ? SSR_STORAGE : window.sessionStorage
            ),
            skipHydration: true,
            // ATENÇÃO: `card`, `cardErrors` e `receiverErrors` estão fora daqui
            // DE PROPÓSITO — dado de cartão não vai para storage do browser
            // (ver a nota em `CheckoutVolatile`). Ao adicionar campo novo,
            // pergunte antes se ele pode sobreviver a um reload.
            partialize: (state): PersistedDraft => ({
                address: state.address,
                shipping: state.shipping,
                slot: state.slot,
                receiver: state.receiver,
                payment: state.payment,
                observations: state.observations,
                orderAttemptKey: state.orderAttemptKey,
                confirmation: state.confirmation,
                orderItems: state.orderItems,
                paymentLabel: state.paymentLabel,
                // Carimba a loja a cada gravação (ver `merge`).
                storeId: getActiveStoreId(),
            }),
            /**
             * Rascunho é de UMA loja. Frete é cotado por loja, as formas de
             * pagamento vêm da loja e o preço dos itens muda com ela — trocar
             * de loja e reencontrar o frete da anterior selecionado fecharia
             * pedido com dado que não vale mais.
             *
             * Trocar de loja recarrega a página (StoreSwitcherModal/StoreInit),
             * então a reidratação é exatamente o ponto onde isso se pega.
             * `storeId` 0 = loja ainda não resolvida: aí não dá para afirmar
             * divergência, e manter o rascunho é o lado seguro do erro.
             */
            merge: (persisted, current) => {
                const draft = persisted as PersistedDraft | undefined;
                if (!draft) return current;

                const active = getActiveStoreId();
                const other = active > 0 && draft.storeId > 0 && draft.storeId !== active;

                return other ? current : { ...current, ...draft };
            },
        }
    )
);

/** Recebedor EFETIVO — o par de valores do modo ativo. */
export const selectReceiver = (state: CheckoutState): ReceiverData =>
    state.receiver.mode === "other" ? state.receiver.other : state.receiver.self;

/** Recebedor efetivo, reativo (o `selectReceiver` para fora de componentes). */
export const useCheckoutReceiver = (): ReceiverData => useCheckoutStore(selectReceiver);

/** Leitura não-reativa do recebedor efetivo (handlers, submits). */
export const getCheckoutReceiver = (): ReceiverData => selectReceiver(useCheckoutStore.getState());
